import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import type { Extension } from '@codemirror/state';
import { tokens } from '@coreai/fluentui-react';
import { ottoTokens } from '@/theme/ottoTokens';

type Theme = 'light' | 'dark';

/**
 * Creates a CodeMirror theme that integrates with Fluent UI design tokens.
 * Uses CSS custom properties set by FluentProvider for runtime theme switching.
 */
export const createFluentTheme = (mode: Theme): Extension[] => {
  const isDark = mode === 'dark';

  const editorTheme = EditorView.theme(
    {
      '&': {
        backgroundColor: 'var(--colorNeutralBackground1)',
        color: 'var(--colorNeutralForeground1)',
        fontSize: '13px',
        fontFamily: 'var(--fontFamilyMonospace)',
        height: '100%',
      },
      '.cm-scroller': {
        overflow: 'auto',
        fontFamily: 'inherit',
        // Custom scrollbar — matches global customScrollbarStyles in src/styles/scrollbar.ts
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--colorNeutralStroke1) transparent',
      },
      '.cm-scroller::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '.cm-scroller::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
        borderRadius: 'var(--borderRadiusMedium)',
      },
      '.cm-scroller::-webkit-scrollbar-thumb': {
        backgroundColor: 'var(--colorNeutralStroke1)',
        borderRadius: 'var(--borderRadiusMedium)',
        border: '2px solid transparent',
        backgroundClip: 'content-box',
      },
      '.cm-scroller::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'var(--colorNeutralStroke1Hover)',
      },
      '.cm-scroller::-webkit-scrollbar-corner': {
        backgroundColor: 'transparent',
      },
      '.cm-content': {
        caretColor: 'var(--colorBrandForeground1)',
        paddingBlock: tokens.spacingVerticalS,
        paddingInline: 0,
      },
      '&.cm-focused': {
        outline: 'none',
      },
      '&.cm-focused .cm-cursor': {
        borderLeftColor: 'var(--colorBrandForeground1)',
        borderLeftWidth: '2px',
      },
      '.cm-gutters': {
        backgroundColor: 'var(--colorNeutralBackground2)',
        color: 'var(--colorNeutralForeground3)',
        borderRight: '1px solid var(--colorNeutralStroke1)',
      },
      '.cm-lineNumbers .cm-gutterElement': {
        paddingTop: 0,
        paddingRight: tokens.spacingHorizontalXS,
        paddingBottom: 0,
        paddingLeft: tokens.spacingHorizontalS,
        minWidth: '20px',
      },
      '.cm-activeLineGutter': {
        backgroundColor: 'var(--colorNeutralBackground1Hover)',
      },
      '.cm-activeLine': {
        backgroundColor: 'var(--otto-editor-active-line)',
      },
      '.cm-selectionBackground': {
        backgroundColor: 'var(--otto-editor-selection)',
      },
      '&.cm-focused .cm-selectionBackground': {
        backgroundColor: 'var(--otto-editor-selection-focused)',
      },
      '.cm-foldPlaceholder': {
        backgroundColor: 'var(--colorNeutralBackground3)',
        color: 'var(--colorNeutralForeground2)',
        border: 'none',
        paddingBlock: 0,
        paddingInline: tokens.spacingHorizontalXS,
        borderRadius: tokens.borderRadiusSmall,
      },
      '.cm-foldGutter .cm-gutterElement': {
        cursor: 'pointer',
        color: 'var(--colorNeutralForeground3)',
        paddingBlock: 0,
        paddingInline: tokens.spacingHorizontalXXS,
        width: '12px',
      },
      '.cm-foldGutter .cm-gutterElement:hover': {
        color: 'var(--colorNeutralForeground1)',
      },
      // Lint styling
      '.cm-lintRange-error': {
        backgroundImage: 'none',
        textDecoration: 'wavy underline var(--colorPaletteRedForeground1)',
        textDecorationSkipInk: 'none',
      },
      '.cm-lintRange-warning': {
        backgroundImage: 'none',
        textDecoration: 'wavy underline var(--colorPaletteYellowForeground1)',
        textDecorationSkipInk: 'none',
      },
      '.cm-lint-marker': {
        width: '8px',
        height: '8px',
      },
      '.cm-lint-marker-error': {
        content: '""',
      },
      '.cm-diagnostic': {
        paddingBlock: tokens.spacingVerticalXS,
        paddingInline: tokens.spacingHorizontalS,
        marginLeft: '0',
        borderRadius: 'var(--borderRadiusMedium)',
      },
      '.cm-diagnostic-error': {
        backgroundColor: 'var(--colorPaletteRedBackground1)',
        borderLeft: '3px solid var(--colorPaletteRedForeground1)',
        color: 'var(--colorNeutralForeground1)',
      },
      '.cm-diagnostic-warning': {
        backgroundColor: 'var(--colorPaletteYellowBackground1)',
        borderLeft: '3px solid var(--colorPaletteYellowForeground1)',
        color: 'var(--colorNeutralForeground1)',
      },
      // Autocomplete styling
      '.cm-tooltip': {
        backgroundColor: 'var(--colorNeutralBackground1)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: 'var(--borderRadiusMedium)',
        boxShadow: 'var(--shadow16)',
        zIndex: 1100,
      },
      '.cm-tooltip-autocomplete': {
        '& > ul': {
          fontFamily: 'var(--fontFamilyMonospace)',
          fontSize: '13px',
        },
        '& > ul > li': {
          paddingBlock: tokens.spacingVerticalXS,
          paddingInline: tokens.spacingHorizontalS,
        },
        '& > ul > li[aria-selected]': {
          backgroundColor: 'var(--colorNeutralBackground1Selected)',
          color: 'var(--colorNeutralForeground1)',
        },
      },
      '.cm-completionLabel': {
        color: 'var(--colorNeutralForeground1)',
      },
      '.cm-completionDetail': {
        color: 'var(--colorNeutralForeground3)',
        marginLeft: tokens.spacingHorizontalS,
        fontStyle: 'italic',
      },
      '.cm-completionMatchedText': {
        color: 'var(--colorBrandForeground1)',
        fontWeight: tokens.fontWeightSemibold,
        textDecoration: 'none',
      },
      // Search panel styling
      '.cm-panels': {
        backgroundColor: 'var(--colorNeutralBackground2)',
        borderBottom: '1px solid var(--colorNeutralStroke1)',
        color: 'var(--colorNeutralForeground1)',
      },
      '.cm-panels.cm-panels-top': {
        borderBottom: '1px solid var(--colorNeutralStroke1)',
      },
      '.cm-panel.cm-search': {
        paddingBlock: tokens.spacingVerticalSNudge,
        paddingInline: tokens.spacingHorizontalS,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: tokens.spacingHorizontalSNudge,
      },
      '.cm-panel.cm-search input, .cm-panel.cm-search button, .cm-panel.cm-search label': {
        fontFamily: 'var(--fontFamilyBase)',
        fontSize: tokens.fontSizeBase200,
      },
      '.cm-panel.cm-search input[type="text"]': {
        backgroundColor: 'var(--colorNeutralBackground1)',
        color: 'var(--colorNeutralForeground1)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: 'var(--borderRadiusMedium)',
        paddingBlock: tokens.spacingVerticalXXS,
        paddingInline: tokens.spacingHorizontalSNudge,
        margin: '0',
      },
      '.cm-panel.cm-search input[type="text"]:focus': {
        outline: 'none',
        borderColor: 'var(--colorBrandStroke1)',
      },
      '.cm-panel.cm-search button': {
        backgroundColor: 'var(--colorNeutralBackground1)',
        color: 'var(--colorNeutralForeground1)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: 'var(--borderRadiusMedium)',
        paddingBlock: tokens.spacingVerticalXXS,
        paddingInline: tokens.spacingHorizontalS,
        cursor: 'pointer',
        backgroundImage: 'none',
        textTransform: 'none',
      },
      '.cm-panel.cm-search button:hover': {
        backgroundColor: 'var(--colorNeutralBackground1Hover)',
      },
      '.cm-panel.cm-search button[name="close"]': {
        color: 'var(--colorNeutralForeground2)',
        background: 'transparent',
        border: 'none',
        fontSize: tokens.fontSizeBase400,
        paddingBlock: 0,
        paddingInline: tokens.spacingHorizontalXS,
      },
      '.cm-panel.cm-search label': {
        color: 'var(--colorNeutralForeground2)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXXS,
      },
      '.cm-searchMatch': {
        backgroundColor: 'var(--otto-editor-search-match)',
      },
      '.cm-searchMatch-selected': {
        backgroundColor: 'var(--otto-editor-search-match-selected)',
      },
    },
    { dark: isDark }
  );

  // Syntax highlighting colors — driven by Otto extension tokens (per-theme CSS vars)
  const syntaxColors = HighlightStyle.define([
    // Shared tokens (JSON + JS)
    { tag: tags.string, color: ottoTokens.codeSyntaxString },
    { tag: tags.number, color: ottoTokens.codeSyntaxNumber },
    { tag: tags.bool, color: ottoTokens.codeSyntaxBoolean },
    { tag: tags.null, color: ottoTokens.codeSyntaxNull },
    { tag: tags.propertyName, color: ottoTokens.codeSyntaxPropertyName },
    { tag: tags.punctuation, color: ottoTokens.codeSyntaxPunctuation },
    { tag: tags.bracket, color: ottoTokens.codeSyntaxPunctuation },

    // JavaScript keywords
    { tag: tags.keyword, color: ottoTokens.codeSyntaxKeyword },
    { tag: tags.controlKeyword, color: ottoTokens.codeSyntaxControlKeyword },

    // Functions and variables
    { tag: tags.function(tags.variableName), color: ottoTokens.codeSyntaxFunction },
    { tag: tags.variableName, color: ottoTokens.codeSyntaxVariable },
    { tag: tags.definition(tags.variableName), color: ottoTokens.codeSyntaxDefinition },

    // Comments
    { tag: tags.lineComment, color: ottoTokens.codeSyntaxComment, fontStyle: 'italic' },
    { tag: tags.blockComment, color: ottoTokens.codeSyntaxComment, fontStyle: 'italic' },

    // Operators
    { tag: tags.operator, color: ottoTokens.codeSyntaxOperator },
    { tag: tags.compareOperator, color: ottoTokens.codeSyntaxOperator },
    { tag: tags.logicOperator, color: ottoTokens.codeSyntaxOperator },

    // Types and classes
    { tag: tags.typeName, color: ottoTokens.codeSyntaxTypeName },
    { tag: tags.className, color: ottoTokens.codeSyntaxTypeName },

    // Regex
    { tag: tags.regexp, color: ottoTokens.codeSyntaxRegexp },

    // Template literals
    { tag: tags.special(tags.string), color: ottoTokens.codeSyntaxString },
  ]);

  return [editorTheme, syntaxHighlighting(syntaxColors)];
};
